/**
 * Wearable ETD (Explosive Trace Detection) Industry Configuration — stub (2026-07-02).
 *
 * Security/defense vertical. Body-worn explosive/IED trace detection fleet for
 * security personnel at airports, borders and venues (NOT medical). Full
 * configuration lives DB-side (industry_profiles + dynamic card registry); this
 * stub registers the vertical with the static picker so it's discoverable.
 *
 * Replaces the mis-scoped biosensor_ide_wearable vertical (which was framed as
 * medical — actual requirement is explosive trace detection).
 *
 * HARD GATE: alerts are ALWAYS human-verified (secondary screening). The
 * AlertEscalationAgent NEVER auto-actions on a positive detection — every
 * threat alert routes to a human operator for verification. HITL non-negotiable.
 */

import { IndustryConfig } from '../industryTypes'

export const wearableEtdConfig: IndustryConfig = {
  id: 'wearable_etd',
  name: 'Wearable ETD (Explosive Trace Detection)',
  description: 'Body-worn explosive trace detection fleet — vapor sniffer badges + handheld swab readers for checkpoint, border and venue screening. TSA/ECAC certified, human-verified alerts (HITL non-negotiable).',
  icon: 'Radar',

  terminology: {
    asset: 'Detector',
    assets: 'Detectors',
    assetPlural: 'ETD Units',
    assetType: 'Detector Class',
    failure: 'Detection Failure',
    health: 'Unit Health',
    healthy: 'Mission-Ready',
    degraded: 'Sensitivity-Drift',
    critical: 'Pull From Service',
    alert: 'Threat Alert',
    process: 'Screening Workflow',
    maintenance: 'Calibration & Servicing',
    inspection: 'Verification Run',
    downtime: 'Unit Out-of-Service',
    workOrder: 'Service Ticket',
    prediction: 'Readiness Forecast',
    anomaly: 'Sensitivity Drift',
    trend: 'Detection Trend',
    risk: 'Missed-Detection Risk',
    facility: 'Checkpoint',
    facilityPlural: 'Checkpoints',
    location: 'Terminal / Checkpoint',
    operator: 'ETD Technician',
    supervisor: 'Security Ops Commander',
    calibration: 'Daily Verification (ECAC CEP)',
    compliance: 'TSA / ECAC ETD Compliance',
  },

  assetTypes: [
    { id: 'vapor_sniffer_badge', name: 'Vapor Sniffer Badge', category: 'body_worn' },
    { id: 'swab_reader', name: 'Handheld Swab Reader', category: 'handheld' },
    { id: 'sampling_pump', name: 'Body-Worn Sampling Pump', category: 'body_worn' },
    { id: 'calibration_station', name: 'Calibration & Verification Station', category: 'qa_equipment' },
    { id: 'docking_gateway', name: 'Docking / Telemetry Gateway', category: 'connectivity' },
  ] as any,

  complianceStandards: [
    { id: 'tsa_etd_cert', name: 'TSA ETD Certification — 49 CFR 1544/1546', authority: 'US TSA' },
    { id: 'ecac_cep', name: 'ECAC CEP — Common Evaluation Process for ETD', authority: 'ECAC' },
    { id: 'dhs_safety_act', name: 'DHS SAFETY Act Designation', authority: 'US DHS' },
    { id: 'nij_0601', name: 'NIJ Standard 0601.02 — Trace Detection', authority: 'US NIJ' },
    { id: 'astm_e2520', name: 'ASTM E2520 — ETD Performance Practice', authority: 'ASTM' },
    { id: 'osha_trace', name: 'OSHA 1910 — Trace Chemical Handling', authority: 'US OSHA' },
  ] as any,
} as IndustryConfig

export default wearableEtdConfig
